import { useState } from "react";
import { api, formatCurrency, formatDate, getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { BONUS_TYPES } from "@/lib/employeeConstants";

export const BonusesTab = ({ employee, stats, bonuses, year, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ bonus_type: "exceptionnelle", amount: 0, bonus_date: "", description: "" });

  const handleSave = async () => {
    if (!form.bonus_date || !form.amount) {
      toast.error("Date et montant obligatoires");
      return;
    }
    try {
      await api.addEmployeeBonus(employee.id, { ...form, amount: parseFloat(form.amount) });
      toast.success("Prime ajoutée");
      setDialogOpen(false);
      setForm({ bonus_type: "exceptionnelle", amount: 0, bonus_date: "", description: "" });
      onRefresh();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const handleDelete = async (id) => {
    await api.deleteEmployeeBonus(id);
    toast.success("Supprimé");
    onRefresh();
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Primes {year}</CardTitle>
          <Button size="sm" onClick={() => setDialogOpen(true)} data-testid="add-bonus-btn">
            <Plus className="h-4 w-4 mr-1" /> Ajouter
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
            {Object.entries(BONUS_TYPES).map(([k, label]) => (
              <div key={k} className="border rounded-lg p-3">
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className="text-lg font-semibold">{formatCurrency(stats.bonuses.by_type[k]?.total_amount || 0)}</div>
                <div className="text-xs text-muted-foreground">{stats.bonuses.by_type[k]?.count || 0} prime(s)</div>
              </div>
            ))}
            <div className="border rounded-lg p-3 bg-primary/5 md:col-span-3">
              <div className="text-xs text-muted-foreground">Total {year}</div>
              <div className="text-2xl font-bold">{formatCurrency(stats.bonuses.total.total_amount)}</div>
              <div className="text-xs text-muted-foreground">{stats.bonuses.total.count} prime(s)</div>
            </div>
          </div>
          {bonuses.length === 0 ? <p className="text-center text-muted-foreground py-4">Aucune prime.</p> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Montant</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bonuses.map(b => (
                  <TableRow key={b.id}>
                    <TableCell>{BONUS_TYPES[b.bonus_type]}</TableCell>
                    <TableCell>{formatDate(b.bonus_date)}</TableCell>
                    <TableCell className="font-semibold">{formatCurrency(b.amount)}</TableCell>
                    <TableCell>{b.description || "-"}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(b.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvelle prime</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Type</Label>
              <Select value={form.bonus_type} onValueChange={v => setForm({ ...form, bonus_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(BONUS_TYPES).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date</Label>
                <Input type="date" value={form.bonus_date} onChange={e => setForm({ ...form, bonus_date: e.target.value })} />
              </div>
              <div>
                <Label>Montant (€)</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSave} data-testid="save-bonus-btn">Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BonusesTab;
